import { useCallback, useEffect, useReducer } from "react";
import {
  Location,
  BilletGroup,
  SpecialLocation,
  GroupTotal,
  LocationTotal,
} from "../../models";
import { getLocations } from "../../repository/BilletYardInventoryRepository";
import { parseLocationsResponse } from "../../utils/parseLocationsResponse";

interface State {
  locations: Location[];
  groups: BilletGroup[];
  specialLocations: SpecialLocation[];
  groupsByLocationId: Record<number, BilletGroup[]>;
  groupTotalsByLocationId: Record<number, Record<string, GroupTotal>>;
  locationTotalsById: Record<number, LocationTotal>;
  isLoading: boolean;
  isRefreshing: boolean;
  error: string;
}

type Action =
  | { type: "START_FETCH"; payload: { isRefresh: boolean } }
  | {
      type: "SET_DATA_SUCCESS";
      payload: {
        locations: Location[];
        groups: BilletGroup[];
        specialLocations: SpecialLocation[];
        groupsByLocationId: Record<number, BilletGroup[]>;
        groupTotalsByLocationId: Record<number, Record<string, GroupTotal>>;
        locationTotalsById: Record<number, LocationTotal>;
      };
    }
  | { type: "SET_ERROR"; payload: string };

const initialState: State = {
  locations: [],
  groups: [],
  specialLocations: [],
  groupsByLocationId: {},
  groupTotalsByLocationId: {},
  locationTotalsById: {},
  isLoading: true,
  isRefreshing: false,
  error: "",
};

function dataReducer(state: State, action: Action): State {
  switch (action.type) {
    case "START_FETCH":
      return {
        ...state,
        isLoading: !action.payload.isRefresh,
        isRefreshing: action.payload.isRefresh,
        error: "",
      };
    case "SET_DATA_SUCCESS":
      return {
        ...state,
        locations: action.payload.locations,
        groups: action.payload.groups,
        specialLocations: action.payload.specialLocations,
        groupsByLocationId: action.payload.groupsByLocationId,
        groupTotalsByLocationId: action.payload.groupTotalsByLocationId,
        locationTotalsById: action.payload.locationTotalsById,
        isLoading: false,
        isRefreshing: false,
      };
    case "SET_ERROR":
      return {
        ...state,
        error: action.payload,
        isLoading: false,
        isRefreshing: false,
      };
    default:
      return state;
  }
}

export const useAllLocationsData = () => {
  const [state, dispatch] = useReducer(dataReducer, initialState);

  // isRefresh: Values: true (called by timer/refresh) or false (initial load)

  const fetchData = useCallback(async (isRefresh = false) => {
    // Single state update for loading/refreshing + error reset
    dispatch({ type: "START_FETCH", payload: { isRefresh } });

    try {
      const response = await getLocations();

      // Use the shared parsing utility function
      const parsedData = parseLocationsResponse(response);

      if (parsedData) {
        // Filter out special locations (LayoutOrder = -1) and sort regular locations
        // This matches the old system behavior where regular and special locations are separate
        const sortedLocations = [...parsedData.locations]
          .filter((location) => location.LayoutOrder >= 1) // Only regular locations (42 total)
          .sort((a, b) => a.LayoutOrder - b.LayoutOrder);

        // Single state update for all data + completion
        dispatch({
          type: "SET_DATA_SUCCESS",
          payload: {
            locations: sortedLocations,
            groups: parsedData.groups,
            specialLocations: parsedData.specialLocations,
            groupsByLocationId: parsedData.groupsByLocationId,
            groupTotalsByLocationId: parsedData.groupTotalsByLocationId,
            locationTotalsById: parsedData.locationTotalsById,
          },
        });
      } else {
        dispatch({ type: "SET_ERROR", payload: "Failed to load locations" });
      }
    } catch (error: unknown) {
      dispatch({
        type: "SET_ERROR",
        payload:
          error instanceof Error ? error.message : "Failed to load locations",
      });
    }
  }, []);

  // Auto-fetch on mount. On mount: Component needs data immediately
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const setError = useCallback((error: string) => {
    dispatch({ type: "SET_ERROR", payload: error });
  }, []);

  return {
    locations: state.locations,
    groups: state.groups,
    specialLocations: state.specialLocations,
    groupsByLocationId: state.groupsByLocationId,
    groupTotalsByLocationId: state.groupTotalsByLocationId,
    locationTotalsById: state.locationTotalsById,
    isLoading: state.isLoading,
    isRefreshing: state.isRefreshing,
    error: state.error,
    setError,
    fetchData,
  };
};
